// @ts-nocheck
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Calendar,
  Clock,
  Download,
  Filter,
  ArrowLeft,
  BarChart3,
  TrendingUp,
  CheckCircle
} from "lucide-react";
import { useOneOnOneSessions } from "@/hooks/api-hooks";
import { useAuth } from "@/contexts/auth-context";

export function OneOnOneHistoryPage() {
  const { user } = useAuth();
  const { data: sessionsData, isLoading } = useOneOnOneSessions();

  const sessions = sessionsData?.data || [];
  const completedSessions = sessions.filter(session => session.status === 'completed');

  // Mock analytics data
  const sessionFrequencyData = [
    { name: 'Jan', sessions: 4 },
    { name: 'Feb', sessions: 6 },
    { name: 'Mar', sessions: 5 },
    { name: 'Apr', sessions: 8 },
    { name: 'May', sessions: 7 },
    { name: 'Jun', sessions: 9 },
    { name: 'Jul', sessions: 8 },
  ];

  const sessionDurationData = [
    { name: '15 min', sessions: 3 },
    { name: '30 min', sessions: 12 },
    { name: '45 min', sessions: 5 },
    { name: '60 min', sessions: 8 },
    { name: '90+ min', sessions: 2 },
  ];

  const actionItemCompletionData = [
    { name: 'Jan', completed: 70 },
    { name: 'Feb', completed: 65 },
    { name: 'Mar', completed: 80 },
    { name: 'Apr', completed: 75 },
    { name: 'May', completed: 85 },
    { name: 'Jun', completed: 90 },
    { name: 'Jul', completed: 88 },
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getActionItemsProgress = (actionItems: any[] = []) => {
    if (actionItems.length === 0) return { completed: 0, total: 0, percentage: 0 };

    const completed = actionItems.filter(item => item.status === 'completed').length;
    const total = actionItems.length;
    const percentage = total > 0 ? (completed / total) * 100 : 0;

    return { completed, total, percentage };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading session history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/one-on-ones">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center">
              <BarChart3 className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">1-on-1 History & Analytics</h1>
              <p className="text-muted-foreground text-lg">
                Review past sessions and analyze 1-on-1 effectiveness
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline">
              <Filter className="mr-2 h-4 w-4" />
              Filter
            </Button>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="history" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="history">Session History</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Past Sessions ({completedSessions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {completedSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">No completed sessions</h3>
                    <p className="text-muted-foreground">
                      Completed sessions will appear here once you've finished some 1-on-1s.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {completedSessions.map((session) => {
                      const dateTime = formatDate(session.scheduledDate);
                      const progress = getActionItemsProgress(session.actionItems);

                      return (
                        <div
                          key={session.id}
                          className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-border/80 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl flex items-center justify-center">
                              <CheckCircle className="h-6 w-6 text-green-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">{session.title}</h3>
                              <p className="text-sm text-muted-foreground">
                                {session.employee?.firstName} {session.employee?.lastName}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              <span>{dateTime.date}</span>
                              <Clock className="h-4 w-4 ml-2" />
                              <span>{dateTime.time}</span>
                            </div>

                            <Badge variant="outline" className="text-xs">
                              {session.duration} min
                            </Badge>

                            <div className="text-sm">
                              {progress.total === 0 ? (
                                <span className="text-muted-foreground">No action items</span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="text-foreground">
                                    {progress.completed}/{progress.total} completed
                                  </span>
                                  <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-green-500 transition-all"
                                      style={{ width: `${progress.percentage}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>

                            <Link to={`/one-on-ones/${session.id}`}>
                              <Button variant="outline" size="sm">
                                View
                              </Button>
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Session Frequency Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Session Frequency</CardTitle>
                </CardHeader>
                <CardContent className="h-80">
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <TrendingUp className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Sessions by Month</h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        {sessionFrequencyData.map((data, index) => (
                          <div key={index} className="text-center">
                            <div className="font-medium">{data.name}</div>
                            <div className="text-muted-foreground">{data.sessions}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Session Duration Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Session Duration Distribution</CardTitle>
                </CardHeader>
                <CardContent className="h-80">
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <BarChart3 className="h-12 w-12 text-green-600 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Duration Breakdown</h3>
                      <div className="space-y-2 text-sm">
                        {sessionDurationData.map((data, index) => (
                          <div key={index} className="flex justify-between">
                            <span>{data.name}</span>
                            <span className="font-medium">{data.sessions} sessions</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Item Completion Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Action Item Completion Rate</CardTitle>
                </CardHeader>
                <CardContent className="h-80">
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <CheckCircle className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Completion Trend</h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        {actionItemCompletionData.slice(-6).map((data, index) => (
                          <div key={index} className="text-center">
                            <div className="font-medium">{data.name}</div>
                            <div className="text-muted-foreground">{data.completed}%</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Session Insights */}
              <Card>
                <CardHeader>
                  <CardTitle>Session Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg border-border">
                      <h3 className="font-semibold text-foreground mb-2">Consistency</h3>
                      <p className="text-sm text-muted-foreground">
                        You've maintained consistent 1-on-1s with your team members, averaging 7.5 sessions per month.
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg border-border">
                      <h3 className="font-semibold text-foreground mb-2">Action Item Follow-through</h3>
                      <p className="text-sm text-muted-foreground">
                        Action item completion rate has improved from 70% to 88% over the past 6 months.
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg border-border">
                      <h3 className="font-semibold text-foreground mb-2">Session Duration</h3>
                      <p className="text-sm text-muted-foreground">
                        Most productive sessions are 30-45 minutes long, with clear agendas and action items.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
